import React, { useEffect, useRef, useState } from "react";
import Avatar from "@/components/chat/Avatar";
import api, { attachmentUrl } from "@/lib/api";
import { dayLabel, dayKey, timeLabel, linkifyParts } from "@/lib/format";
import { Check, CheckCheck, FileText, Download, Ban, SmilePlus, Heart, CornerUpLeft, Pencil, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger, PopoverClose } from "@/components/ui/popover";
import { toast } from "sonner";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏", "🎉", "🔥"];
const EMOJI_EXTENDED = [
  "😀","😁","😅","🤣","😊","😍","😘","😜","🤪","🤗","🤔","😐","😴","😎","🥳","😇",
  "🙂","🙃","😉","😏","😌","😔","😞","😤","😡","🤯","😱","😨","😭","🥺","😢","😮‍💨",
  "👍","👎","👏","🙌","🙏","💪","👀","🤝","✌️","🤞","👌","🤙","👋","🫡","🫶","💯",
  "❤️","🧡","💛","💚","💙","💜","🖤","🤍","💔","❣️","💕","💞","✨","⭐","🔥","🎉",
  "✅","❌","⚠️","❓","❗","💤","💡","📌","📎","📝","💰","📈","📉","⏰","☕","🍕",
];

function MessageText({ text, query }) {
  if (!text) return null;
  const nodes = [];
  const pushLinkified = (str, keyBase) => {
    linkifyParts(str).forEach((p, i) => {
      if (p.t === "link") {
        nodes.push(
          <a key={`${keyBase}-l${i}`} href={p.v} target="_blank" rel="noopener noreferrer nofollow"
            className="underline break-all hover:opacity-80">{p.v}</a>
        );
      } else {
        nodes.push(<span key={`${keyBase}-t${i}`}>{p.v}</span>);
      }
    });
  };
  if (query && query.trim()) {
    const q = query.trim();
    const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const segs = text.split(re);
    segs.forEach((seg, i) => {
      if (seg.toLowerCase() === q.toLowerCase()) {
        nodes.push(<mark key={`m${i}`} className="search-hl">{seg}</mark>);
      } else {
        pushLinkified(seg, `s${i}`);
      }
    });
  } else {
    pushLinkified(text, "x");
  }
  return <span className="chat-msg-text whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{nodes}</span>;
}

function Receipt({ msg, otherIds }) {
  if (otherIds.length === 0) return <Check className="h-3.5 w-3.5 opacity-70" />;
  const readAll = otherIds.every((id) => (msg.readBy || []).includes(id));
  const deliveredAny = otherIds.some((id) => (msg.deliveredTo || []).includes(id));
  if (readAll) return <CheckCheck className="h-3.5 w-3.5 text-sky-300" data-testid="receipt-read" />;
  if (deliveredAny) return <CheckCheck className="h-3.5 w-3.5 opacity-70" data-testid="receipt-delivered" />;
  return <Check className="h-3.5 w-3.5 opacity-70" data-testid="receipt-sent" />;
}

function Attachment({ att, mine, onLoad, onOpenImage, onDownload }) {
  const url = attachmentUrl(att.id);
  if (att.isImage) {
    return (
      <button type="button" onClick={(e) => { e.stopPropagation(); onOpenImage(att); }} className="block mt-1 w-full" data-testid={`attachment-image-${att.id}`}>
        <img src={url} alt={att.filename} loading="lazy" onLoad={onLoad}
          className="rounded-lg max-h-60 object-cover border border-black/10 cursor-pointer hover:opacity-95 transition-opacity" />
      </button>
    );
  }
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onDownload(att); }} data-testid={`attachment-file-${att.id}`}
      className={`flex items-center gap-3 p-2.5 rounded-lg my-1 text-xs border transition-colors w-full text-left ${mine ? "bg-white/15 border-white/20 hover:bg-white/25" : "bg-background/60 border-border hover:bg-muted"}`}>
      <FileText className="h-6 w-6 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate">{att.filename}</div>
        <div className="opacity-70">{(att.size / 1024).toFixed(0)} KB</div>
      </div>
      <Download className="h-4 w-4 flex-shrink-0" />
    </button>
  );
}

export default function MessageList({ messages, me, members, isGroup, query, currentMatchId, convId, onReact, onLoadMore, hasMore, loadingMore, selectMode, selectedIds, onToggleSelect, onReply, onEdit, textScale }) {
  const bottomRef = useRef(null);
  const containerRef = useRef(null);
  const matchRefs = useRef({});
  const prevLen = useRef(0);
  const lastTap = useRef({ id: null, time: 0 });
  const [heartPop, setHeartPop] = useState(null);
  const [reactors, setReactors] = useState(null);
  const [viewer, setViewer] = useState(null);
  const pressTimer = useRef(null);
  const longPressed = useRef(false);
  const dragRef = useRef({ id: null, startX: 0, startY: 0, x: 0, active: false });
  const [drag, setDrag] = useState({ id: null, x: 0 });
  const otherIds = members.filter((m) => m.id !== me.id).map((m) => m.id);

  const downloadAtt = async (att) => {
    try {
      const res = await api.get(`/attachments/${att.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename || "allegato";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      toast.error("Download non riuscito");
    }
  };

  const quickHeart = (id) => {
    onReact(id, "❤️");
    setHeartPop(id);
    setTimeout(() => setHeartPop((cur) => (cur === id ? null : cur)), 700);
  };

  const onBubbleTap = (id) => {
    const now = Date.now();
    const prev = lastTap.current;
    if (prev.id === id && now - prev.time < 320) {
      lastTap.current = { id: null, time: 0 };
      quickHeart(id);
    } else {
      lastTap.current = { id, time: now };
    }
  };

  const swipeStart = (e, id) => {
    if (selectMode) return;
    const t = e.touches[0];
    dragRef.current = { id, startX: t.clientX, startY: t.clientY, x: 0, active: false };
  };
  const swipeMove = (e, id) => {
    const d = dragRef.current;
    if (d.id !== id) return;
    const t = e.touches[0];
    const dx = t.clientX - d.startX;
    const dy = t.clientY - d.startY;
    if (!d.active) {
      if (Math.abs(dx) < 10) return;
      if (Math.abs(dy) > Math.abs(dx)) { dragRef.current.id = null; return; }
      d.active = true;
    }
    const x = Math.max(0, Math.min(dx, 90));
    d.x = x;
    setDrag({ id, x });
  };
  const swipeEnd = (m) => {
    const d = dragRef.current;
    if (d.id === m.id && d.active && d.x > 55 && !m.deletedForAll) onReply(m);
    dragRef.current = { id: null, startX: 0, startY: 0, x: 0, active: false };
    setDrag({ id: null, x: 0 });
  };

  // Reset scroll behaviour when switching conversation
  useEffect(() => { prevLen.current = 0; }, [convId]);

  // Keep the latest note visible when the iOS keyboard toggles (viewport resize)
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => { bottomRef.current?.scrollIntoView({ behavior: "auto" }); };
    vv.addEventListener("resize", onResize);
    return () => vv.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (messages.length > prevLen.current) {
      const firstLoad = prevLen.current === 0;
      const added = messages.length - prevLen.current;
      if (firstLoad) {
        // apri sempre in fondo (ultimo messaggio), senza animazione
        bottomRef.current?.scrollIntoView({ behavior: "auto" });
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "auto" }), 60);
      } else if (added <= 2) {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
    prevLen.current = messages.length;
  }, [messages]);

  useEffect(() => {
    if (currentMatchId && matchRefs.current[currentMatchId]) {
      matchRefs.current[currentMatchId].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [currentMatchId]);

  const onScroll = (e) => {
    if (e.target.scrollTop < 60 && hasMore && !loadingMore) onLoadMore();
  };

  const maybeScrollOnMedia = () => {
    const c = containerRef.current;
    if (!c) return;
    const nearBottom = c.scrollHeight - c.scrollTop - c.clientHeight < 260;
    if (nearBottom) bottomRef.current?.scrollIntoView({ behavior: "auto" });
  };

  const memberName = (id) => members.find((m) => m.id === id)?.displayName || "Utente";

  let lastDay = null;
  const rows = [];
  messages.forEach((m) => {
    const k = dayKey(m.createdAt);
    if (k !== lastDay) {
      lastDay = k;
      rows.push(
        <div key={`d-${k}`} className="my-4 flex items-center justify-center relative z-10">
          <span className="bg-muted text-muted-foreground text-[11px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full border border-border/50 shadow-sm">
            {dayLabel(m.createdAt)}
          </span>
        </div>
      );
    }
    const mine = m.senderId === me.id;
    const deleted = m.deletedForAll;
    const isSel = selectMode && selectedIds?.has(m.id);
    rows.push(
      <div key={m.id} ref={(el) => (matchRefs.current[m.id] = el)}
        className={`relative flex ${mine ? "justify-end" : "justify-start"} mb-1.5 px-1 msg-in`}
        data-testid={`message-${m.id}`}
        onTouchStart={(e) => swipeStart(e, m.id)}
        onTouchMove={(e) => swipeMove(e, m.id)}
        onTouchEnd={() => swipeEnd(m)}>
        {!deleted && drag.id === m.id && drag.x > 4 && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-sky-500 pointer-events-none" style={{ opacity: Math.min(1, drag.x / 55) }}>
            <CornerUpLeft className="h-5 w-5" />
          </div>
        )}
        {selectMode && !deleted && (
          <div className={`self-center flex-shrink-0 ${mine ? "order-last ml-2" : "mr-2"}`}>
            <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${isSel ? "bg-sky-600 border-sky-600" : "border-muted-foreground/40"}`}>
              {isSel && <Check className="h-3.5 w-3.5 text-white" />}
            </div>
          </div>
        )}
        {!mine && isGroup && !deleted && (
          <div className="mr-2 self-end mb-1"><Avatar name={memberName(m.senderId)} isGroup={false} showStatus={false} size={30} /></div>
        )}
        <div className={`group relative max-w-[85%] md:max-w-[70%] min-w-0`}
          style={drag.id === m.id ? { transform: `translateX(${drag.x}px)`, transition: "none" } : { transition: "transform .2s ease-out" }}>
          <div onClick={() => { if (deleted) return; if (selectMode) { onToggleSelect(m.id); } else { onBubbleTap(m.id); } }}
            className={`relative select-none touch-manipulation p-2.5 px-3 shadow-sm text-sm leading-relaxed ${isSel ? "ring-2 ring-sky-500 ring-offset-1 ring-offset-[hsl(var(--chat-bg))]" : ""} ${
            deleted
              ? "bg-muted text-muted-foreground italic rounded-2xl"
              : mine
                ? "bg-sky-600 text-white rounded-2xl rounded-tr-sm cursor-pointer"
                : "bg-card text-card-foreground border border-border/60 rounded-2xl rounded-tl-sm cursor-pointer"
          }`}>
            {!mine && isGroup && !deleted && (
              <div className="text-[11px] font-semibold mb-0.5 text-sky-600 dark:text-sky-400">{memberName(m.senderId)}</div>
            )}
            {deleted ? (
              <span className="flex items-center gap-1"><Ban className="h-3.5 w-3.5" /> Nota eliminata</span>
            ) : (
              <>
                {m.replyTo && (
                  <button type="button"
                    onClick={(e) => { e.stopPropagation(); matchRefs.current[m.replyTo.id]?.scrollIntoView({ behavior: "smooth", block: "center" }); }}
                    data-testid={`reply-quote-${m.id}`}
                    className={`block w-full text-left mb-1.5 rounded-md px-2 py-1 border-l-4 ${mine ? "bg-white/15 border-white/50" : "bg-muted border-sky-400"}`}>
                    <div className={`text-[11px] font-semibold truncate ${mine ? "text-white/90" : "text-sky-600 dark:text-sky-400"}`}>{m.replyTo.senderName}</div>
                    <div className={`text-xs truncate ${mine ? "text-white/70" : "text-muted-foreground"}`}>{m.replyTo.text || "📎 Allegato"}</div>
                  </button>
                )}
                {m.attachment && <Attachment att={m.attachment} mine={mine} onLoad={maybeScrollOnMedia} onOpenImage={setViewer} onDownload={downloadAtt} />}
                {m.text && <MessageText text={m.text} query={query} />}
              </>
            )}
            <div className={`flex items-center gap-1 justify-end mt-1 text-[10px] font-mono ${mine ? "text-white/70" : "text-muted-foreground"}`}>
              {m.editedAt && <span className="italic opacity-80" data-testid={`edited-label-${m.id}`}>modificato</span>}
              {timeLabel(m.createdAt)}
              {mine && !deleted && <Receipt msg={m} otherIds={otherIds} />}
            </div>
            {heartPop === m.id && (
              <Heart className="heart-pop absolute left-1/2 top-1/2 h-14 w-14 text-red-500 fill-red-500 pointer-events-none drop-shadow-lg" data-testid={`heart-pop-${m.id}`} />
            )}
          </div>
          {!deleted && m.reactions && Object.keys(m.reactions).length > 0 && (
            <div className={`flex flex-wrap gap-1 mt-1 ${mine ? "justify-end" : "justify-start"}`} data-testid={`reactions-${m.id}`}>
              {Object.entries(m.reactions).map(([emoji, ids]) => {
                const reacted = (ids || []).includes(me.id);
                return (
                  <button key={emoji}
                    onClick={() => { if (longPressed.current) { longPressed.current = false; return; } onReact(m.id, emoji); }}
                    onPointerDown={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const names = (ids || []).map(memberName);
                      longPressed.current = false;
                      clearTimeout(pressTimer.current);
                      pressTimer.current = setTimeout(() => {
                        longPressed.current = true;
                        setReactors({ emoji, names, top: rect.top, left: rect.left });
                      }, 450);
                    }}
                    onPointerUp={() => clearTimeout(pressTimer.current)}
                    onPointerLeave={() => clearTimeout(pressTimer.current)}
                    onContextMenu={(e) => e.preventDefault()}
                    data-testid={`reaction-chip-${m.id}`}
                    className={`flex items-center gap-1 text-xs rounded-full px-2 py-0.5 border transition-colors select-none ${reacted ? "bg-sky-100 dark:bg-sky-900/50 border-sky-400 text-sky-700 dark:text-sky-300" : "bg-muted border-border text-muted-foreground hover:bg-muted-foreground/10"}`}>
                    <span className="leading-none">{emoji}</span><span className="font-semibold">{(ids || []).length}</span>
                  </button>
                );
              })}
            </div>
          )}
          {!deleted && !selectMode && (
            <div className={`absolute top-1 ${mine ? "-left-9" : "-right-9"} opacity-80 hover:opacity-100 transition-opacity`}>
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid={`react-button-${m.id}`} className="h-8 w-8 rounded-full bg-muted border border-border/60 shadow-sm hover:bg-muted-foreground/20 flex items-center justify-center">
                    <SmilePlus className="h-4 w-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align={mine ? "start" : "end"} className="w-64 p-2" data-testid={`react-picker-${m.id}`}>
                  <PopoverClose asChild>
                    <button onClick={() => onReply(m)} data-testid={`reply-button-${m.id}`}
                      className="flex items-center gap-2 w-full text-left text-sm px-2 py-1.5 mb-1 rounded hover:bg-muted">
                      <CornerUpLeft className="h-4 w-4 text-sky-500" /> Rispondi
                    </button>
                  </PopoverClose>
                  {mine && m.text && (
                    <PopoverClose asChild>
                      <button onClick={() => onEdit(m)} data-testid={`edit-button-${m.id}`}
                        className="flex items-center gap-2 w-full text-left text-sm px-2 py-1.5 mb-1 rounded hover:bg-muted">
                        <Pencil className="h-4 w-4 text-amber-500" /> Modifica
                      </button>
                    </PopoverClose>
                  )}
                  <div className="flex gap-0.5 justify-between mb-1 border-t border-border pt-1.5">
                    {QUICK_REACTIONS.map((e) => (
                      <button key={e} onClick={() => onReact(m.id, e)} data-testid={`react-emoji-${m.id}`}
                        className="text-xl leading-none p-1 rounded hover:bg-muted hover:scale-125 transition-transform">
                        {e}
                      </button>
                    ))}
                  </div>
                  <div className="border-t border-border pt-1.5 grid grid-cols-8 gap-0.5 max-h-40 overflow-y-auto thin-scroll" data-testid={`react-grid-${m.id}`}>
                    {EMOJI_EXTENDED.map((e, i) => (
                      <button key={`${e}-${i}`} onClick={() => onReact(m.id, e)} data-testid={`react-emoji-ext-${m.id}`}
                        className="text-lg leading-none p-1 rounded hover:bg-muted transition-colors">
                        {e}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
      </div>
    );
  });

  return (
    <div ref={containerRef} onScroll={onScroll} className={`flex-1 min-w-0 min-h-0 overflow-y-auto overflow-x-hidden thin-scroll px-3 py-4 chat-scale-${textScale || 1}`} data-testid="message-list">
      {loadingMore && <div className="text-center text-xs text-muted-foreground py-2">Caricamento...</div>}
      {rows}
      <div ref={bottomRef} />
      {reactors && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setReactors(null)} data-testid="reactors-overlay" />
          <div className="fixed z-50 w-56 max-w-[80vw] bg-popover text-popover-foreground border border-border rounded-xl shadow-lg p-3 text-xs"
            style={{ top: Math.max(8, reactors.top - 8), left: Math.min(Math.max(8, reactors.left), (typeof window !== "undefined" ? window.innerWidth - 232 : reactors.left)), transform: "translateY(-100%)" }}
            data-testid="reactors-popover">
            <div className="font-semibold mb-1.5 flex items-center gap-1.5">
              <span className="text-base leading-none">{reactors.emoji}</span>
              <span className="text-muted-foreground">{reactors.names.length}</span>
            </div>
            <div className="space-y-1 max-h-44 overflow-y-auto thin-scroll">
              {reactors.names.map((n, i) => (
                <div key={i} className="truncate">{n}</div>
              ))}
            </div>
          </div>
        </>
      )}
      {viewer && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex flex-col" data-testid="attachment-viewer" onClick={() => setViewer(null)}>
          <div className="flex items-center justify-end gap-2 p-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
            <button type="button" onClick={(e) => { e.stopPropagation(); downloadAtt(viewer); }} data-testid="attachment-viewer-download"
              className="h-10 w-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center">
              <Download className="h-5 w-5" />
            </button>
            <button type="button" onClick={(e) => { e.stopPropagation(); setViewer(null); }} data-testid="attachment-viewer-close"
              className="h-10 w-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 min-h-0 flex items-center justify-center p-4">
            <img src={attachmentUrl(viewer.id)} alt={viewer.filename} onClick={(e) => e.stopPropagation()}
              className="max-h-full max-w-full object-contain rounded-lg" data-testid="attachment-viewer-image" />
          </div>
          <div className="text-center text-white/70 text-xs p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] truncate">{viewer.filename}</div>
        </div>
      )}
    </div>
  );
}
